// ─────────────────────────────────────────────────────────────
// AdminToolbar.js
// Dark overlay bar shown at the top of an event card when an
// admin is logged in. Provides quick actions without needing
// to go to the admin panel.
//
// Props:
//   event        — the event object
//   adminUser    — Firebase auth user (null if not admin)
//   onConfirm    — () => void
//   onCancel     — () => void
//   onSaveAttendance — (eventId, value) => void
// ─────────────────────────────────────────────────────────────

import React, { useState } from 'react';
import { Link } from 'react-router-dom';

const formatEventLabel = (event) => {
  if (!event) return null;
  try {
    const d = event.date?.toDate ? event.date.toDate() : new Date(event.date);
    return `${event.title} — ${d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', timeZone: 'America/Los_Angeles' })}`;
  } catch { return event.title || null; }
};

function AdminToolbar({ event, adminUser, onConfirm, onCancel, onSaveAttendance }) {
  const [editingAttendance, setEditingAttendance] = useState(false);
  const [attendanceValue, setAttendanceValue] = useState('');

  if (!adminUser) return null;

  return (
    <div className="admin-event-toolbar">
      <span className="admin-toolbar-label">Admin</span>

      <button
        className={`admin-toolbar-btn${event.isConfirmed ? ' active-confirm' : ''}`}
        onClick={() => onConfirm(event.id, event.isConfirmed, formatEventLabel(event))}
      >
        {event.isConfirmed ? '✓ Confirmed' : 'Confirm'}
      </button>

      <button
        className={`admin-toolbar-btn${event.isCanceled ? ' active-cancel' : ''}`}
        onClick={() => onCancel(event.id, event.isCanceled, formatEventLabel(event))}
      >
        {event.isCanceled ? '✕ Canceled' : 'Cancel'}
      </button>

      <Link to="/admin" className="admin-toolbar-btn">
        Edit Event
      </Link>

      {/* Inline attendance editor */}
      <div className="admin-attendance-inline">
        <span className="admin-toolbar-label" style={{ marginLeft: '0.5rem' }}>Actual:</span>
        {editingAttendance ? (
          <>
            <input
              type="number"
              min="0"
              value={attendanceValue}
              onChange={e => setAttendanceValue(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  onSaveAttendance(event.id, attendanceValue, formatEventLabel(event));
                  setEditingAttendance(false);
                }
                if (e.key === 'Escape') setEditingAttendance(false);
              }}
              className="admin-attendance-input"
              autoFocus
            />
            <button
              className="admin-toolbar-btn active-confirm"
              onClick={() => {
                onSaveAttendance(event.id, attendanceValue, formatEventLabel(event));
                setEditingAttendance(false);
              }}
            >✓</button>
          </>
        ) : (
          <span
            className="admin-attendance-value"
            onClick={() => {
              setAttendanceValue(event.actualAttendance ?? '');
              setEditingAttendance(true);
            }}
          >
            {event.actualAttendance ?? '—'}
          </span>
        )}
      </div>
    </div>
  );
}

export default AdminToolbar;
